import { useRouter } from 'next/router';
import { UrlObject as BaseUrlObject } from 'url';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ParsedUrlQuery } from 'node:querystring';

export type UrlObject = Omit<BaseUrlObject, 'query'> & { query: ParsedUrlQuery };

export type useRouterStateMapper<StateType extends { [key: string]: any }> = (
    state: StateType
) => UrlObject;
export type useRouterStateParser<StateType extends { [key: string]: any }> = (
    url: UrlObject
) => StateType;

export type useRouterStateProps<StateType extends { [key: string]: any }> = {
    mapper: useRouterStateMapper<StateType>;
    parser: useRouterStateParser<StateType>;
    initialValue: StateType;
};

export type useRouterStateResult<StateType extends { [key: string]: any }> = [
    StateType,
    (newState: StateType) => void,
    (newState: StateType) => UrlObject,
    boolean,
];

const isSameURL = (url1: UrlObject, url2: UrlObject): boolean => {
    return JSON.stringify(url1) === JSON.stringify(url2);
};

export const useRouterStatePart = <
    StateType extends { [key: string]: any },
    Key extends keyof StateType,
>(
    state: StateType,
    setState: (newState: StateType) => any,
    getChangeLink: (newState: StateType) => UrlObject,
    key: Key
): [
    StateType[Key],
    (newValue: StateType[Key]) => void,
    (newValue: StateType[Key]) => UrlObject,
] => {
    const value = useMemo(() => {
        return state[key];
    }, [state, key]);

    const getChange = useCallback(
        (newValue: StateType[Key]): StateType => {
            return { ...state, [key]: newValue };
        },
        [state, key]
    );

    const setValue = useCallback(
        (newValue: StateType[Key]) => {
            return setState(getChange(newValue));
        },
        [getChange, setState]
    );

    const getValueChangeLink = useCallback(
        (newValue: StateType[Key]) => {
            return getChangeLink(getChange(newValue));
        },
        [getChange, getChangeLink]
    );

    return [value, setValue, getValueChangeLink];
};

export const useRouterState = <StateType extends { [key: string]: any }>({
    initialValue,
    parser,
    mapper,
}: useRouterStateProps<StateType>): useRouterStateResult<StateType> => {
    const router = useRouter();
    const hydrated = useRef(false);
    const [isReady, setIsReady] = useState(false);
    const [value, setValue] = useState<StateType>(initialValue);
    const previousValueRef = useRef<UrlObject>(mapper(initialValue));

    useEffect(() => {
        if (hydrated.current === true && router.isReady === true) {
            const currentRoute: UrlObject = {
                pathname: router.pathname,
                query: router.query,
            };
            if (isSameURL(currentRoute, previousValueRef.current)) {
                return;
            }
            setValue(parser(currentRoute));
        }
    }, [router, parser]);

    useEffect(() => {
        if (hydrated.current === false && router.isReady === true) {
            hydrated.current = true;
            setIsReady(true);
            const currentRoute: UrlObject = {
                pathname: router.pathname,
                query: router.query,
            };
            if (isSameURL(currentRoute, previousValueRef.current)) {
                return;
            }
            setValue(parser(currentRoute));
        }
    }, [router, parser]);

    useEffect(() => {
        if (router.isReady === false || hydrated.current === false) {
            return;
        }
        const newMappedURL = mapper(value);
        if (isSameURL(newMappedURL, previousValueRef.current)) {
            return;
        }
        previousValueRef.current = newMappedURL;
        router.push(newMappedURL, undefined, { shallow: true });
    }, [router, mapper, value]);

    return [value, setValue, mapper, isReady];
};

export default useRouterState;
